import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Popconfirm,
  Progress,
  Row,
  Space,
  Typography,
  Upload,
  UploadProps
} from "antd";
import { CloudUploadOutlined, DeleteOutlined, FileOutlined, ImportOutlined } from "@ant-design/icons";
import { FetchApiInterface } from "../../../../../interfaces/FetchApi.interface";
import { AxiosResponse } from "axios";
import { ResponseInterface } from "../../../../../interfaces/Response.interface";
import useFetch from "../../../../../hooks/useFetch";
import useMessageUser from "../../../../../hooks/useMessageUser";
import { useTranslation } from "react-i18next";
import ButtonDownloadReport from "../../../../Components/ButtonDownloadReport";
import { AUTH_TOKEN } from "../../../../../config/config";
import RegionDpaIds from "../../../../Components/Updates/RegionDpaIdsUtils";

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

const SCUChildData = () => {

  const [fileList, setFileList] = useState<any[]>([]);
  const [selectedDpaId, setSelectedDpaId] = useState<number | number[] | null>(null);
  const [selectedDpaName, setSelectedDpaName] = useState<string | string[] | null>(null);
  const [deleteAndReinsert, setDeleteAndReinsert] = useState<boolean>(false);
  const { fetchApi, loading } = useFetch();
  const { showMessage } = useMessageUser();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const props: UploadProps = {
    onRemove: file => {
      setFileList(fileList.filter(item => item.uid !== file.uid));
    },
    beforeUpload: file => {
      const isXML = file.name.endsWith('.xml');
      if (!isXML) {
        showMessage('codeExeptionExtensionFile', 'error');
        return false;
      }
      const newFile = {
        uid: file.uid,
        name: file.name,
        status: "normal",
        percent: 0,
        file: file,
      };
      setFileList([newFile]);
      return false;
    },
    fileList: [],
  };

  const confirmDelete = (fileItem: any) => {
    setFileList(fileList.filter(file => file.uid !== fileItem.uid));
  };

  const renderItem = (file: any, index: number) => {
    return (
      <Row key={index} className='mb-1'>
        <Col span={24}>
          <Card>
            <Row align="middle" gutter={[10, 0]}>
              <Col>
                <FileOutlined />
              </Col>
              <Col flex={1}>
                <Row>
                  <Col span={24}>{file.name}</Col>
                  <Col span={24}>
                    <Progress percent={file?.percent} status={file?.status} />
                    {(file?.percent === 100 && file?.status !== 'done') && <Paragraph>
                      {
                        (file?.status === 'exception') ? (
                          `${file?.error}`
                        ) : (
                          t('processingFile')  // Reemplaza 'Procesando el archivo...'
                        )
                      }
                    </Paragraph>}
                  </Col>
                </Row>
              </Col>
              <Col>
                <Popconfirm
                  title={t('deleteFile')}  // Reemplaza 'Eliminar archivo'
                  onConfirm={() => confirmDelete(file)}
                  okText={t('yes')}  // Reemplaza 'Sí'
                  cancelText={t('no')}  // Reemplaza 'No'
                >
                  <Button size='small' icon={<DeleteOutlined />} danger />
                </Popconfirm>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    );
  };

  const onFinish = async () => {
    let url = '/comms/save_child_info';
    for (let i = 0; i < fileList.length; i++) {
      const fileToUpdate = fileList[i];
      // Establecer el estado de este archivo específico como 'active' y reiniciar el progreso
      setFileList(fileList.map(file =>
        file.uid === fileToUpdate.uid ? { ...file, status: 'active', percent: 0 } : file
      ));

      const formData = new FormData();
      formData.append('file', fileToUpdate?.file);
      formData.append('dpaId', selectedDpaId?.toString() || '');
      formData.append('deleteAndReinsert', deleteAndReinsert ? 'true' : 'false');

      const jwtToken = localStorage.getItem(AUTH_TOKEN);
      const myHeaders = new Headers();
      myHeaders.append('Content-Type', 'application/json');
      myHeaders.append('Authorization', `Bearer ${jwtToken}`);
      const requestOptions: any = {
        method: 'POST',
        headers: myHeaders,
        body: selectedDpaId?.toString() || '',
        redirect: 'follow',
      };

      // Validación local del nombre del archivo con el dpaName seleccionado
      const fileName = fileToUpdate?.file?.name;
      const fileNameParts = fileName.split('_');
      //const fileNameDpaName = fileNameParts[fileNameParts.length - 1].split('.')[0].toUpperCase();
      const lastPart = fileNameParts[fileNameParts.length - 1] ?? '';
      const fileNameDpaName = lastPart.replace(/\.[^/.]+$/, '').trim().toUpperCase();
      // Obtener el nombre del DPA seleccionado (puede ser string o array)
      const currentDpaName = Array.isArray(selectedDpaName) ? selectedDpaName[0] : selectedDpaName;
      
      if (!currentDpaName || fileNameDpaName !== currentDpaName.toUpperCase()) {
        showMessage('El nombre del archivo no coincide con el nombre del DPA', 'error');
        // Establecer el estado del archivo como 'exception'
        setFileList(fileList.map(file =>
          file.uid === fileToUpdate.uid ? { ...file, status: 'exception', error: 'Nombre de archivo no coincide con DPA', percent: 100 } : file
        ));
        continue; // Continuar con el siguiente archivo
      }
      
      // Proceder con la subida del archivo si la validación local pasa
      const dataSend: FetchApiInterface = {
        url,
        data: formData,
        formData: true
      };

      try {
        // Simular el progreso del archivo
        for (let percent = 0; percent <= 100; percent++) {
          setFileList(fileList.map(file =>
            file.uid === fileToUpdate.uid ? { ...file, percent: percent } : file
          ));
          // Aquí deberías simular una espera, esto es solo un ejemplo
          await new Promise(resolve => setTimeout(resolve, 3));
        }

        const { data }: AxiosResponse<ResponseInterface> = await fetchApi(dataSend);

        if (data.status === 'success') {
          // Establecer el estado de este archivo específico como 'done' cuando la carga es exitosa
          setFileList(fileList.map(file =>
            file.uid === fileToUpdate.uid ? { ...file, status: 'done', percent: 100 } : file
          ));
          showMessage(data?.message, 'success');
          
          // Limpiar la lista de archivos después de 2 segundos para permitir nueva selección
          setTimeout(() => {
            setFileList([]);
          }, 2000);
        } else {
          showMessage(data?.message, 'info');
          // Si no es exitoso, puedes querer establecer un estado diferente aquí
        }
      } catch (reason: any) {
        // Establecer el estado de este archivo específico como 'exception' si hay un error
        setFileList(fileList.map(file =>
          file.uid === fileToUpdate.uid ? { ...file, status: 'exception', error: reason?.message, percent: 100 } : file
        ));
        showMessage(reason?.message, 'error');
      }
    }
  };

  const regionDpaIdsChange = (dpaId: number | number[], dpaName: string | string[]) => {
    console.log(dpaId, dpaName);
    setSelectedDpaId(dpaId);
    setSelectedDpaName(dpaName);
  };

  return (
    <div className='p-2'>
      <Row>
        <Col span={24}>
          <Row justify='space-between'>
            <Col>
              <Title level={5}>{t('scuXmlFromChildData')}:</Title>
            </Col>
            <Col>
              <ButtonDownloadReport reportId={4} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Paragraph className='text-gray'>
            {t('loremIpsumText')}
          </Paragraph>
        </Col>
        <Col span={24}>
          {
            fileList.map((file: File, index: number) => renderItem(file, index))
          }
        </Col>
        <Col span={24} className='mt-1'>
          <div>
            <Form form={form}>

              <RegionDpaIds onChange={regionDpaIdsChange} isRequired={true} />

              <Form.Item className='mb-1'>
                <Checkbox checked={deleteAndReinsert} onChange={(e) => setDeleteAndReinsert(e.target.checked)}>
                  Eliminar y volver a ingresar los registros
                </Checkbox>
              </Form.Item>

            </Form>
          </div>
          <div>
            <Title level={5}>{t('childDataXml')}:</Title>
          </div>
          <div>
            <Dragger {...props}>
              <div className='p-2'>
                <Space>
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined style={{ fontSize: '15px' }} /> {t('dragAndDrop')} <Text strong underline className='color-primary'>{t('selectFile')}</Text>
                  </p>
                </Space>
              </div>
            </Dragger>
          </div>
        </Col>
        <Col span={24} className='text-right mt-1'>
          <Button type='primary' icon={<ImportOutlined />} shape='round' loading={loading} disabled={!(fileList.length > 0 && selectedDpaId)} onClick={onFinish}>{t('import')}</Button>
        </Col>
      </Row>
    </div>
  );
}

export default SCUChildData;
